<template>
  <k-form
    :columns="3"
    class="learner-links-form"
    @submit.prevent="handleSubmit"
  >
    <k-form-section legend="Link details" :column-span="2">
      <k-form-field label="Category" field-id="learner-link-category">
        <k-dropdown
          id="learner-link-category"
          :options="categoryOptions"
          :model-value="modelValue.link_category ?? ''"
          :show-search="false"
          aria-label="Category"
          @update:model-value="selectCategory($event || null)"
        />
      </k-form-field>
      <k-form-field label="Label" field-id="learner-link-label">
        <input
          id="learner-link-label"
          :value="modelValue.label"
          type="text"
          class="form-control"
          placeholder='Optional - links without a label will show as "External Link" and the URL will be hidden'
          @input="updateField('label', $event.target.value)"
        />
      </k-form-field>
      <k-form-field label="URL" :required="true" field-id="learner-link-url">
        <input
          id="learner-link-url"
          :value="modelValue.url"
          type="url"
          class="form-control"
          :aria-invalid="Boolean(modelValue.url?.trim() && !isUrlValid)"
          :aria-describedby="urlFieldDescribedBy"
          @input="updateField('url', $event.target.value)"
        />
        <span
          v-if="modelValue.url?.trim() && !isUrlValid"
          id="learner-link-url-error"
          class="helper-text"
          aria-live="polite"
        >
          {{ urlValidationMessage }}
        </span>
      </k-form-field>
    </k-form-section>
    <k-form-section legend="Icon" :column-span="1" class="learner-link-icon-section">
      <div
        class="learner-link-icon-options"
        role="radiogroup"
        aria-label="Icon"
      >
        <button
          v-for="icon in iconPresets"
          :key="icon"
          type="button"
          class="learner-link-icon-option"
          :class="{ selected: modelValue.icon === icon }"
          :style="iconOptionStyle(icon)"
          role="radio"
          :aria-label="getLearnerLinkIconLabel(icon)"
          :aria-checked="modelValue.icon === icon"
          @click="updateField('icon', icon)"
        >
          <i :class="getLearnerLinkIconClasses(icon)" aria-hidden="true"></i>
        </button>
      </div>
      <div
        class="learner-link-background-options"
        role="radiogroup"
        aria-label="Background colour"
      >
        <button
          v-for="color in colorPresets"
          :key="color"
          type="button"
          class="learner-link-background-option"
          :class="{ selected: modelValue.icon_color === color }"
          :style="{ backgroundColor: colorCssVar(color) }"
          role="radio"
          :aria-label="getLearnerLinkColorLabel(color)"
          :aria-checked="modelValue.icon_color === color"
          @click="updateField('icon_color', color)"
        ></button>
      </div>
    </k-form-section>
    <k-form-section legend="Visibility" :column-span="2" class="learner-link-visibility-section">
      <k-form-field>
        <k-toggle
          :model-value="shareWithLearner"
          :disabled="isShareLocked"
          size="small"
          label="Share on learner homepage"
          @update:model-value="updateField('share_with_learner', $event)"
        />
        <span v-if="isShareLocked" class="helper-text">
          Links in this category are never shared with the learner
        </span>
      </k-form-field>
    </k-form-section>
  </k-form>
</template>

<style scoped>
.learner-link-icon-section {
  grid-row: span 2;
  padding: 12px;
}

.learner-link-icon-section :deep(fieldset) {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  min-height: 0;
}

.learner-link-icon-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-content: flex-start;
  padding-bottom: 12px;
}

.learner-link-background-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.learner-link-background-option {
  width: 20px;
  height: 20px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
}

.learner-link-background-option.selected {
  border-color: var(--kate-type-primary);
}

.learner-link-icon-option {
  width: 40px;
  height: 40px;
  border: 2px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--kate-background-body);
  color: var(--kate-type-light);
}

.learner-link-icon-option i {
  font-size: 1rem;
}

.learner-link-icon-option.selected {
  color: var(--kate-type-dark);
}

@media (max-width: 768px) {
  .learner-link-icon-section {
    grid-row: span 1;
    padding: 16px;
  }
}

.helper-text {
  display: block;
  font-size: 0.9em;
  color: var(--kate-type-secondary) !important;
  margin-top: 4px;
  font-style: italic;
}
</style>

<script>
import {
  KForm,
  KFormField,
  KFormSection,
  KToggle,
} from '@base-components/forms';
import KDropdown from '@base-components/k-dropdown.vue';
import {
  defaultLearnerLinkColor,
  getLearnerLinkIconClasses,
  getLearnerLinkIconLabel as resolveLearnerLinkIconLabel,
  getLearnerLinkColorLabel as resolveLearnerLinkColorLabel,
  learnerLinkCategories,
  learnerLinkColorCssVars,
  learnerLinkColorPresets,
  learnerLinkIconPresets,
  normalizeLearnerLinkUrl,
  isLearnerLinkUrlInputValid,
  validateLearnerLinkUrl,
} from '@utils/learner-link-presets';

export default {
  components: {
    KDropdown,
    KForm,
    KFormField,
    KFormSection,
    KToggle,
  },

  props: {
    modelValue: {
      type: Object,
      required: true,
    },
  },

  emits: ['update:modelValue', 'submit'],

  data() {
    return {
      iconPresets: learnerLinkIconPresets,
      colorPresets: learnerLinkColorPresets,
      // k-dropdown matches options by id, and an empty string stands in for the null "None" category
      categoryOptions: [
        { id: '', name: 'None' },
        ...Object.entries(learnerLinkCategories).map(([id, { label }]) => ({ id, name: label })),
      ],
    };
  },

  computed: {
    categoryRules() {
      return learnerLinkCategories[this.modelValue.link_category];
    },

    isShareLocked() {
      return Boolean(this.categoryRules?.shareWithLearnerLock);
    },

    shareWithLearner() {
      return this.isShareLocked ? this.categoryRules.shareWithLearner : this.modelValue.share_with_learner;
    },

    selectedColorCssVar() {
      return this.colorCssVar(this.modelValue.icon_color || defaultLearnerLinkColor);
    },

    normalizedUrl() {
      return normalizeLearnerLinkUrl(this.modelValue.url);
    },

    urlValidation() {
      if (!this.modelValue.url?.trim()) {
        return { isValid: false, message: '' };
      }
      return validateLearnerLinkUrl(this.normalizedUrl);
    },

    isUrlValid() {
      return this.urlValidation.isValid;
    },

    urlValidationMessage() {
      return this.urlValidation.message;
    },

    validForm() {
      return isLearnerLinkUrlInputValid(this.modelValue.url);
    },

    urlFieldDescribedBy() {
      return this.modelValue.url?.trim() && !this.isUrlValid ? 'learner-link-url-error' : undefined;
    },
  },

  methods: {
    colorCssVar(color) {
      const cssVar = learnerLinkColorCssVars[color];
      return cssVar ? `var(${cssVar})` : `var(${learnerLinkColorCssVars[defaultLearnerLinkColor]})`;
    },

    iconOptionStyle(icon) {
      if (this.modelValue.icon !== icon) {
        return undefined;
      }

      return {
        backgroundColor: this.selectedColorCssVar,
      };
    },

    getLearnerLinkIconClasses(icon) {
      return getLearnerLinkIconClasses(icon);
    },

    getLearnerLinkIconLabel(icon) {
      return resolveLearnerLinkIconLabel(icon);
    },

    getLearnerLinkColorLabel(color) {
      return resolveLearnerLinkColorLabel(color);
    },

    updateField(field, value) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        [field]: value,
      });
    },

    selectCategory(category) {
      const rules = learnerLinkCategories[category];
      this.$emit('update:modelValue', {
        ...this.modelValue,
        link_category: category,
        ...(rules ? { icon: rules.icon, icon_color: rules.color } : {}),
      });
    },

    handleSubmit() {
      if (!this.validForm) {
        return;
      }

      if (this.normalizedUrl !== this.modelValue.url) {
        this.updateField('url', this.normalizedUrl);
      }

      this.$emit('submit');
    },
  },
};
</script>
